import { combineLatest, Subscription } from 'rxjs';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CountryCode } from 'libphonenumber-js';
import { PhoneNumberCountryStatus } from 'src/lib/fliko/helpers/phone-number.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ReceiverData } from 'src/lib/fliko/models/receiver.data';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { RentalUnitVM, ResidentUnitsVM } from 'src/app/view/users/services/models/users-view-models';
import { UsersDataService } from 'src/app/view/users/services/users-data.service';
import { AsyncHlp } from '../../helpers/async.helper';
import { GrammarHelper } from '../../helpers/grammar.helper';
import { MatIconHlp } from '../../helpers/matIcon.helper';
import { UnitFilterDictionary } from '../../models/unit-filter-dictionary.model';
import { DialogService } from '../../services/dialog.service';
import { LocalizationService } from '../../services/localization.service';
import { TableUnitVM } from '../clickable-row-table/models/value-table.model';
import { calculateReceiverPhoneEligibility } from './receiver-phone-eligibility.helper';
import { ReceiversSelectDialogComponent } from './receivers-select-dialog/receivers-select-dialog.component';
import {
  ReceiverSelectDialogData,
  ReceiverSelectDisplayMode,
  ReceiverSelectFilters,
  ReceiverSelectReturnData,
  ReceiverSummaryData
} from './view-models/receivers-select.view-model';

@Component({
  selector: 'app-receivers-select-preview',
  templateUrl: './receivers-select-preview.component.html',
  styleUrls: ['./receivers-select-preview.component.scss']
})
export class ReceiversSelectPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() residentIds: string[] | null = [];
  @Input() residentType: ResidentType | null = null;
  @Input() displayMode: ReceiverSelectDisplayMode = 'email';
  @Input() allowedPhoneCountries: readonly CountryCode[] = [];

  @Output() onSelect = new EventEmitter<void>();

  public locationFilters = UnitFilterDictionary.empty();

  public selectedResidents: ResidentUnitsVM[] | null = [];
  public selectedCount = 0;
  public touched = false;
  public foreignPhoneResidents: ResidentUnitsVM[] = [];
  public invalidPhoneResidents: ResidentUnitsVM[] = [];

  private trans = (x: string) => this.ls.translate(x);

  private allResidentUnitsVMs: ResidentUnitsVM[] = [];
  private residentUnitsVMs: ResidentUnitsVM[] = [];
  private phoneStatuses: ReadonlyMap<string, PhoneNumberCountryStatus> = new Map();
  private intendedResidentIds: string[] | null = [];
  private selectionIntentionInitialized = false;
  private previousFiltersCache?: ReceiverSelectFilters;
  private subscription$ = new Subscription();

  constructor(
    private dialogService: DialogService,
    private userDS: UsersDataService,
    private ctxDS: ContextDataService,
    private ls: LocalizationService
  ) {}

  ngOnInit(): void {
    this.subscription$.add(
      combineLatest([this.userDS.getUnitFilterDictionary$(), this.userDS.getResUnitsVMs$()]).subscribe(
        ([unitFilterDict, residentUnitsVMs]) => {
          this.locationFilters = unitFilterDict;
          this.allResidentUnitsVMs = residentUnitsVMs;
          this.applyPhoneCountryFilter();
        }
      )
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.residentIds || changes.residentType) {
      this.intendedResidentIds = this.residentIds;
      this.selectionIntentionInitialized = true;
    }
    if (changes.allowedPhoneCountries || changes.residentIds || changes.residentType) {
      this.applyPhoneCountryFilter();
    }
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public showDialog(): void {
    this.touched = true;
    const excludedResidentIds = new Set(
      [...this.foreignPhoneResidents, ...this.invalidPhoneResidents].map((residentVM) => residentVM.resident.id)
    );
    this.dialogService
      .openSimpleDialog<ReceiversSelectDialogComponent, ReceiverSelectDialogData, ReceiverSelectReturnData>(
        ReceiversSelectDialogComponent,
        {
          allFilters: this.locationFilters,
          previousFilters: this.previousFiltersCache,
          allResidents: this.allResidentUnitsVMs,
          previousResidents: this.selectedResidents,
          residentType: this.residentType,
          displayMode: this.displayMode,
          phoneStatuses: this.phoneStatuses
        }
      )
      .afterClosed()
      .subscribe((data) => {
        if (data) {
          this.previousFiltersCache = data.selectedFilters;
          this.residentType = data.residentType;
          const excludedIdsToKeep = data.visibleUnavailableResidentIds.filter((residentId) =>
            excludedResidentIds.has(residentId)
          );
          this.intendedResidentIds =
            data.selectedResidentIds === null ? null : Array.from(new Set([...data.selectedResidentIds, ...excludedIdsToKeep]));
          this.selectionIntentionInitialized = true;
          this.applyPhoneCountryFilter(false);
          this.onSelect.emit();
        }
      });
  }

  public addressIcons(units: RentalUnitVM[]): TableUnitVM[] {
    return units.map((u) => {
      return { number: ToString.shortNumber(u.unit), icon: MatIconHlp.get(u.type) };
    });
  }

  // ! USED IN PARENT COMPONENT
  public summaryData(): ReceiverSummaryData {
    const hasCountryRestriction = this.allowedPhoneCountries.length > 0;
    const receiverIds = hasCountryRestriction ? this.submitData(true).residentIds : null;
    return {
      residents: this.selectedResidents ? this.selectedResidents.map((x) => x.resident) : null,
      residentType: this.residentType,
      ...(hasCountryRestriction
        ? {
            availableReceiverCount: receiverIds?.length ?? 0,
            excludedReceiverCount: this.foreignPhoneResidents.length + this.invalidPhoneResidents.length
          }
        : {})
    };
  }

  /**
   * ! USED IN PARENT COMPONENT
   * @param {boolean} idsOnly returns only data for residentIds and adds all landlords / tenants into list
   * @return {ReceiverData}
   */
  public submitData(idsOnly: boolean = false): ReceiverData {
    if (idsOnly) {
      const residentIds = new Set(this.selectedResidents?.map((x) => x.resident.id) ?? []);
      if (this.residentType) {
        this.residentUnitsVMs.forEach((res) => {
          if (this.residentType === res.type) residentIds.add(res.resident.id);
        });
      } else if (!this.residentType && !this.selectedResidents) {
        this.residentUnitsVMs.forEach((res) => residentIds.add(res.resident.id));
      }
      return { residentIds: Array.from(residentIds), residentType: null };
    } else {
      return {
        residentIds: this.selectedResidents ? this.selectedResidents.map((x) => x.resident.id) : null,
        residentType: this.residentType
      };
    }
  }

  // ! USED IN PARENT COMPONENT
  public get invalid(): boolean {
    this.touched = true;
    return this.selectedResidents?.length === 0 || this.selectedCount === 0;
  }

  // ! USED IN PARENT COMPONENT
  public get dirty(): boolean {
    return this.touched || !!this.selectedResidents?.length || !!this.residentType || !!this.selectedCount;
  }

  public get residentsPreview(): string {
    if (this.residentType) {
      return (
        `${this.trans('select.allM')} ` +
        this.trans(GrammarHelper.getResidentKeyInCase(this.ctxDS.peekCurrentEstate()!.type, this.residentType, 'M', true))
      );
    } else {
      return StrHlp.capitalize(`${this.trans('select.allM')} ${this.trans('unit.residents').toLowerCase()}`);
    }
  }

  public get foreignPhoneResidentsPreview(): string {
    return this.phoneResidentsPreview(this.foreignPhoneResidents);
  }

  public get invalidPhoneResidentsPreview(): string {
    return this.phoneResidentsPreview(this.invalidPhoneResidents);
  }

  private phoneResidentsPreview(residents: ResidentUnitsVM[]): string {
    return residents.map((residentVM) => ToString.fullNamePhone(residentVM.resident)).join('; ');
  }

  private updateSelectedResidents(residentIds: string[] | null): void {
    const unitsMap = new Map(this.residentUnitsVMs.map((x) => [x.resident.id, x]));
    this.selectedResidents = [];
    if (residentIds) {
      const selected: ResidentUnitsVM[] = [];
      residentIds.forEach((id) => {
        const resUnit = unitsMap.get(id);
        if (resUnit) selected.push(resUnit);
      });

      this.selectedResidents = selected;
    } else {
      this.selectedResidents = null;
    }
    this.countReceivers();
  }

  private applyPhoneCountryFilter(emitOnCountChange = true): void {
    const selectedIds = this.currentIntendedResidentIds();
    const previousCount = this.selectedCount;
    const eligibility = calculateReceiverPhoneEligibility(this.allResidentUnitsVMs, this.allowedPhoneCountries, {
      residentIds: selectedIds,
      residentType: this.residentType
    });
    this.residentUnitsVMs = eligibility.selectableResidents;
    this.foreignPhoneResidents = eligibility.foreignResidents;
    this.invalidPhoneResidents = eligibility.invalidResidents;
    this.phoneStatuses = eligibility.phoneStatuses;
    this.updateSelectedResidents(selectedIds);
    if (emitOnCountChange && previousCount !== this.selectedCount) {
      AsyncHlp.inNextTick(() => this.onSelect.emit());
    }
  }

  private currentIntendedResidentIds(): string[] | null {
    if (this.selectionIntentionInitialized) return this.intendedResidentIds;
    if (!this.touched) return this.residentIds;
    return this.selectedResidents?.map((residentVM) => residentVM.resident.id) ?? null;
  }

  private countReceivers(): void {
    if (this.selectedResidents) {
      this.selectedCount = this.selectedResidents.length;
    } else {
      if (this.residentType) {
        this.selectedCount = this.residentUnitsVMs.reduce((prev, curr) => (prev += +(curr.type === this.residentType)), 0);
      } else {
        this.selectedCount = this.residentUnitsVMs.length;
      }
    }
  }
}
